// apps/client/src/hooks/useArrears.ts
import { useState, useEffect } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';

interface UseArrearsParams {
  companyId: string;
  year: number;
  yearType: 'calendar' | 'financial';
  brokerId?: string;
  officeId?: string;
  templateId?: string;
  entityCode?: string;
}

export interface ArrearsPayment {
  paymentId: string;
  loanId: string;
  arrearsDate: Date;
  /**
   * 'YYYY-MM' of the INVOICE date — the key a trail-risk arrears event is built
   * on, so it is what a follow-up chip matches. Distinct from `periodMonth`,
   * which is the run's statement period. Kept a string: it is a month, not a
   * point in time, and converting to Date would reintroduce a timezone question.
   */
  invoiceMonth: string | null;
  periodMonth: string;
  commissionCents: number;
  loanBalanceCents: number;
  runNumber: number | null;
}

export interface ArrearsRecord {
  id: string;
  loanFingerprint: string; // For contact tracking linkage
  broker: string;
  brokerId: string;
  customerName: string | null;
  loanId: string;
  lender: string | null;
  settlementDate: Date;
  latestArrearsDate: Date;
  arrearsPaymentCount: number; // Number of arrears payments for this loan
  loanAmountCents: number;
  loanBalanceCents: number;
  totalArrearsCents: number; // Sum of all arrears commissions for this loan
  officeId: string | null;
  originatingOfficeId: string | null;
  payments: ArrearsPayment[]; // Individual arrears payment details
}

interface MonthlyTrend {
  month: string;
  arrearsCount: number;
  arrearsAmountCents: number;
}

export interface BrokerBreakdown {
  brokerId: string;
  brokerDisplay: string;
  arrearsCount: number;
  arrearsAmountCents: number;
}

interface LenderBreakdown {
  lender: string;
  arrearsCount: number;
  arrearsAmountCents: number;
}

interface UseArrearsReturn {
  arrearsLoans: ArrearsRecord[];
  loading: boolean;
  error: string | null;
  totalArrearsPayments: number; // Total individual arrears payment records
  totalArrearsAmountCents: number;
  totalLoanBalanceCents: number; // Balance of loans in arrears
  uniqueLoanCount: number; // Number of unique loans in arrears
  // Total book stats for percentage calculation
  totalBookLoanCount: number;
  totalBookBalanceCents: number;
  arrearsPercentByCount: number; // % of loans in arrears
  arrearsPercentByBalance: number; // % of book balance in arrears
  monthlyTrend: MonthlyTrend[];
  brokerBreakdown: BrokerBreakdown[];
  lenderBreakdown: LenderBreakdown[];
  refetch: () => Promise<void>;
}

/**
 * Hook to fetch arrears report from BigQuery
 *
 * Queries raw_commissions table for loans with arrears commissions.
 * Returns individual arrears records for empathetic client outreach.
 *
 * RBAC: Backend automatically filters to broker's data if role is 'broker'
 */
export function useArrears({
  companyId,
  year,
  yearType,
  brokerId,
  officeId,
  templateId,
  entityCode,
}: UseArrearsParams): UseArrearsReturn {
  const [arrearsLoans, setArrearsLoans] = useState<ArrearsRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalArrearsPayments, setTotalArrearsPayments] = useState(0);
  const [totalArrearsAmountCents, setTotalArrearsAmountCents] = useState(0);
  const [totalLoanBalanceCents, setTotalLoanBalanceCents] = useState(0);
  const [uniqueLoanCount, setUniqueLoanCount] = useState(0);
  const [totalBookLoanCount, setTotalBookLoanCount] = useState(0);
  const [totalBookBalanceCents, setTotalBookBalanceCents] = useState(0);
  const [arrearsPercentByCount, setArrearsPercentByCount] = useState(0);
  const [arrearsPercentByBalance, setArrearsPercentByBalance] = useState(0);
  const [monthlyTrend, setMonthlyTrend] = useState<MonthlyTrend[]>([]);
  const [brokerBreakdown, setBrokerBreakdown] = useState<BrokerBreakdown[]>([]);
  const [lenderBreakdown, setLenderBreakdown] = useState<LenderBreakdown[]>([]);

  const fetchArrears = async () => {
    if (!companyId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      console.log(`[useArrears] Calling BigQuery getArrearsReport:`, {
        companyId,
        year,
        yearType,
        brokerId: brokerId || 'all',
        officeId: officeId || 'all',
        templateId: templateId || 'all',
      });

      const getArrearsReportFunction = httpsCallable(functions, 'getArrearsReport');
      const result = await getArrearsReportFunction({
        companyId,
        year,
        yearType,
        brokerId,
        officeId,
        templateId,
        entityCode,
      });

      const data = result.data as {
        totalArrearsPayments: number;
        totalArrearsAmountCents: number;
        totalLoanBalanceCents: number;
        uniqueLoanCount: number;
        totalBookLoanCount: number;
        totalBookBalanceCents: number;
        arrearsPercentByCount: number;
        arrearsPercentByBalance: number;
        records: Array<{
          id: string;
          loanFingerprint: string;
          brokerId: string;
          brokerDisplay: string;
          customerName: string | null;
          loanId: string;
          lender: string | null;
          settlementDate: string;
          latestArrearsDate: string;
          arrearsPaymentCount: number;
          loanAmountCents: number;
          loanBalanceCents: number;
          totalArrearsCents: number;
          officeId: string | null;
          originatingOfficeId: string | null;
          payments: Array<{
            paymentId: string;
            loanId: string;
            arrearsDate: string;
            // Optional: absent when served by an older backend revision.
            invoiceMonth?: string | null;
            periodMonth: string;
            commissionCents: number;
            loanBalanceCents: number;
            runNumber: number | null;
          }>;
        }>;
        monthlyTrend: MonthlyTrend[];
        brokerBreakdown: BrokerBreakdown[];
        lenderBreakdown: LenderBreakdown[];
      };

      console.log(`[useArrears] BigQuery returned:`, {
        totalArrearsPayments: data.totalArrearsPayments,
        totalArrearsAmountCents: data.totalArrearsAmountCents / 100,
        uniqueLoanCount: data.uniqueLoanCount,
        totalBookLoanCount: data.totalBookLoanCount,
        arrearsPercentByCount: data.arrearsPercentByCount,
        arrearsPercentByBalance: data.arrearsPercentByBalance,
        records: data.records.length,
      });

      // Store aggregate metrics
      setTotalArrearsPayments(data.totalArrearsPayments);
      setTotalArrearsAmountCents(data.totalArrearsAmountCents);
      setTotalLoanBalanceCents(data.totalLoanBalanceCents);
      setUniqueLoanCount(data.uniqueLoanCount);
      setTotalBookLoanCount(data.totalBookLoanCount);
      setTotalBookBalanceCents(data.totalBookBalanceCents);
      setArrearsPercentByCount(data.arrearsPercentByCount);
      setArrearsPercentByBalance(data.arrearsPercentByBalance);
      setMonthlyTrend(data.monthlyTrend || []);
      setBrokerBreakdown(data.brokerBreakdown || []);
      setLenderBreakdown(data.lenderBreakdown || []);

      // Transform records to frontend format (grouped by loan)
      const transformedRecords: ArrearsRecord[] = data.records.map((record) => ({
        id: record.id,
        loanFingerprint: record.loanFingerprint,
        broker: record.brokerDisplay,
        brokerId: record.brokerId,
        customerName: record.customerName,
        loanId: record.loanId,
        lender: record.lender,
        settlementDate: new Date(record.settlementDate),
        latestArrearsDate: record.latestArrearsDate ? new Date(record.latestArrearsDate) : new Date(record.settlementDate),
        arrearsPaymentCount: record.arrearsPaymentCount,
        loanAmountCents: record.loanAmountCents,
        loanBalanceCents: record.loanBalanceCents,
        totalArrearsCents: record.totalArrearsCents,
        officeId: record.officeId,
        originatingOfficeId: record.originatingOfficeId,
        payments: (record.payments || []).map((p) => ({
          paymentId: p.paymentId,
          loanId: p.loanId,
          arrearsDate: new Date(p.arrearsDate),
          invoiceMonth: p.invoiceMonth ?? null,
          periodMonth: p.periodMonth,
          commissionCents: p.commissionCents,
          loanBalanceCents: p.loanBalanceCents,
          runNumber: p.runNumber,
        })),
      }));

      setArrearsLoans(transformedRecords);
    } catch (err: any) {
      console.error('[useArrears] Error:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch arrears data');
      setArrearsLoans([]);
      setTotalArrearsPayments(0);
      setTotalArrearsAmountCents(0);
      setTotalLoanBalanceCents(0);
      setUniqueLoanCount(0);
      setTotalBookLoanCount(0);
      setTotalBookBalanceCents(0);
      setArrearsPercentByCount(0);
      setArrearsPercentByBalance(0);
      setMonthlyTrend([]);
      setBrokerBreakdown([]);
      setLenderBreakdown([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchArrears();
  }, [companyId, year, yearType, brokerId, officeId, templateId, entityCode]);

  return {
    arrearsLoans,
    loading,
    error,
    totalArrearsPayments,
    totalArrearsAmountCents,
    totalLoanBalanceCents,
    uniqueLoanCount,
    totalBookLoanCount,
    totalBookBalanceCents,
    arrearsPercentByCount,
    arrearsPercentByBalance,
    monthlyTrend,
    brokerBreakdown,
    lenderBreakdown,
    refetch: fetchArrears,
  };
}
